import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  LinearProgress,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  Divider,
} from '@mui/material';
import {
  Edit,
  Delete,
  Add,
  Download,
  Refresh,
  EmojiEvents,
  TrendingUp,
  AccountBalance,
  BarChart,
  Save,
  Cancel,
} from '@mui/icons-material';
import { 
  collection, 
  getDocs, 
  doc, 
  setDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  orderBy 
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import { motion } from 'framer-motion';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`leaderboard-tabpanel-${index}`}
      aria-labelledby={`leaderboard-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const LeaderboardManagement = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [leaderboards, setLeaderboards] = useState({
    xmPoints: [],
    xmPercentage: [],
    weltradeVolume: [],
    monthlyExcellence: []
  });
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [entryToDelete, setEntryToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const leaderboardTypes = [
    { 
      id: 'xmPoints', 
      label: 'XM Global - Top Points', 
      icon: <AccountBalance />,
      collection: 'leaderboard_xm_points'
    },
    { 
      id: 'xmPercentage', 
      label: 'XM Global - Percentage Growth', 
      icon: <TrendingUp />,
      collection: 'leaderboard_xm_percentage'
    },
    { 
      id: 'weltradeVolume', 
      label: 'Weltrade - Volume Traded', 
      icon: <BarChart />,
      collection: 'leaderboard_weltrade_volume'
    },
    { 
      id: 'monthlyExcellence', 
      label: 'Monthly Excellence Award', 
      icon: <EmojiEvents />,
      collection: 'leaderboard_monthly_excellence'
    }
  ];

  useEffect(() => {
    fetchAllLeaderboards();
  }, []);

  const fetchAllLeaderboards = async () => {
    setLoading(true);
    try {
      const leaderboardData = {};
      
      for (const type of leaderboardTypes) {
        try {
          const snapshot = await getDocs(query(collection(db, type.collection), orderBy('rank', 'asc')));
          leaderboardData[type.id] = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
        } catch (error) {
          console.log(`No data found for ${type.id}, creating empty array`);
          leaderboardData[type.id] = [];
        }
      }
      
      setLeaderboards(leaderboardData);
    } catch (error) {
      console.error('Error fetching leaderboards:', error);
      showSnackbar('Error fetching leaderboard data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleEditEntry = (entry, leaderboardType) => {
    setSelectedEntry({ ...entry, leaderboardType });
    setEditDialogOpen(true);
  };

  const handleAddEntry = (leaderboardType) => {
    const newEntry = {
      rank: (leaderboards[leaderboardType]?.length || 0) + 1,
      name: '',
      email: '',
      country: '',
      points: 0,
      profit: '',
      trades: 0,
      winRate: 0,
      isVerified: false,
      leaderboardType
    };
    setSelectedEntry(newEntry);
    setAddDialogOpen(true);
  };

  const handleSaveEntry = async (isNew = false) => {
    try {
      const { leaderboardType, ...entryData } = selectedEntry;
      const collectionName = leaderboardTypes.find(t => t.id === leaderboardType)?.collection;
      
      if (!collectionName) {
        throw new Error('Invalid leaderboard type');
      }

      if (isNew) {
        // Add new entry
        const newDocRef = doc(collection(db, collectionName));
        await setDoc(newDocRef, {
          ...entryData,
          createdAt: new Date(),
          updatedAt: new Date()
        });
        
        // Update local state
        setLeaderboards(prev => ({
          ...prev,
          [leaderboardType]: [...prev[leaderboardType], { id: newDocRef.id, ...entryData }]
            .sort((a, b) => a.rank - b.rank)
        }));
        
        setAddDialogOpen(false);
        showSnackbar('Entry added successfully');
      } else {
        // Update existing entry
        const entryRef = doc(db, collectionName, selectedEntry.id);
        await updateDoc(entryRef, {
          ...entryData,
          updatedAt: new Date()
        });
        
        // Update local state
        setLeaderboards(prev => ({
          ...prev,
          [leaderboardType]: prev[leaderboardType].map(entry => 
            entry.id === selectedEntry.id ? { ...entry, ...entryData } : entry
          ).sort((a, b) => a.rank - b.rank)
        }));
        
        setEditDialogOpen(false);
        showSnackbar('Entry updated successfully');
      }
    } catch (error) {
      console.error('Error saving entry:', error);
      showSnackbar('Error saving entry', 'error');
    }
  };

  const handleDeleteEntry = async () => {
    try {
      const collectionName = leaderboardTypes.find(t => t.id === entryToDelete.leaderboardType)?.collection;
      await deleteDoc(doc(db, collectionName, entryToDelete.id));
      
      // Update local state
      setLeaderboards(prev => ({
        ...prev,
        [entryToDelete.leaderboardType]: prev[entryToDelete.leaderboardType].filter(entry => 
          entry.id !== entryToDelete.id
        )
      }));
      
      setDeleteDialogOpen(false);
      setEntryToDelete(null);
      showSnackbar('Entry deleted successfully');
    } catch (error) {
      console.error('Error deleting entry:', error);
      showSnackbar('Error deleting entry', 'error');
    }
  };

  const exportLeaderboard = (leaderboardType) => {
    const data = leaderboards[leaderboardType] || [];
    const typeName = leaderboardTypes.find(t => t.id === leaderboardType)?.label || leaderboardType;
    
    const csvContent = [
      ['Rank', 'Name', 'Email', 'Country', 'Points/Score', 'Profit', 'Trades', 'Win Rate', 'Verified'].join(','),
      ...data.map(entry => [
        entry.rank,
        `"${entry.name || ''}"`,
        `"${entry.email || ''}"`,
        `"${entry.country || ''}"`,
        entry.points || entry.percentageGrowth || entry.lotsTraded || entry.overallScore || 0,
        `"${entry.profit || ''}"`,
        entry.trades || 0,
        entry.winRate || 0,
        entry.isVerified ? 'Yes' : 'No'
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fxwars-${leaderboardType}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const renderLeaderboardTable = (leaderboardType) => {
    const data = leaderboards[leaderboardType] || [];
    
    return (
      <Paper>
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            {leaderboardTypes.find(t => t.id === leaderboardType)?.label} ({data.length} entries)
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              startIcon={<Add />}
              variant="contained"
              onClick={() => handleAddEntry(leaderboardType)}
            >
              Add Entry
            </Button>
            <Button
              startIcon={<Download />}
              variant="outlined"
              onClick={() => exportLeaderboard(leaderboardType)}
              disabled={data.length === 0}
            >
              Export
            </Button>
            <Button
              startIcon={<Refresh />}
              variant="outlined"
              onClick={fetchAllLeaderboards}
              disabled={loading}
            >
              Refresh
            </Button>
          </Stack>
        </Box>
        <Divider />
        
        {loading ? (
          <LinearProgress />
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Rank</TableCell>
                  <TableCell>Trader</TableCell>
                  <TableCell>Performance</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((entry, index) => (
                  <motion.tr
                    key={entry.id}
                    component={TableRow}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                  >
                    <TableCell>
                      <Chip 
                        label={`#${entry.rank}`} 
                        color={entry.rank <= 3 ? 'primary' : 'default'}
                        variant={entry.rank <= 3 ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                          {entry.name?.[0] || '?'}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2">{entry.name}</Typography>
                          <Typography variant="body2" color="textSecondary">
                            {entry.country} • {entry.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.5}>
                        <Typography variant="body2">
                          <strong>
                            {entry.points && `${entry.points} points`}
                            {entry.percentageGrowth && `${entry.percentageGrowth}`}
                            {entry.lotsTraded && `${entry.lotsTraded} lots`}
                            {entry.overallScore && `${entry.overallScore}/100`}
                          </strong>
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Profit: {entry.profit} • Trades: {entry.trades} • Win Rate: {entry.winRate}%
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={entry.isVerified ? 'Verified' : 'Unverified'} 
                        size="small" 
                        color={entry.isVerified ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="Edit Entry">
                          <IconButton 
                            size="small" 
                            onClick={() => handleEditEntry(entry, leaderboardType)}
                            color="primary"
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Entry">
                          <IconButton 
                            size="small" 
                            onClick={() => {
                              setEntryToDelete({ ...entry, leaderboardType });
                              setDeleteDialogOpen(true);
                            }}
                            color="error"
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </motion.tr>
                ))}
                {data.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography color="textSecondary">
                        No entries found. Click "Add Entry" to create the first entry.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    );
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">
          Leaderboard Management
        </Typography>
      </Box>

      {/* Stats Overview */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {leaderboardTypes.map((type) => (
          <Grid item xs={12} sm={6} md={3} key={type.id}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {type.icon}
                  <Box sx={{ ml: 2 }}>
                    <Typography color="textSecondary" variant="overline">
                      {type.label}
                    </Typography>
                    <Typography variant="h5" fontWeight="bold">
                      {leaderboards[type.id]?.length || 0}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      entries
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Leaderboard Tabs */}
      <Paper>
        <Tabs 
          value={currentTab} 
          onChange={(e, newValue) => setCurrentTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {leaderboardTypes.map((type, index) => (
            <Tab 
              key={type.id}
              icon={type.icon} 
              label={type.label} 
              iconPosition="start"
            />
          ))}
        </Tabs>
        
        {leaderboardTypes.map((type, index) => (
          <TabPanel key={type.id} value={currentTab} index={index}>
            {renderLeaderboardTable(type.id)}
          </TabPanel>
        ))}
      </Paper>

      {/* Edit Entry Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Leaderboard Entry</DialogTitle>
        <DialogContent>
          {selectedEntry && (
            <Stack spacing={3} sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Rank"
                    type="number"
                    value={selectedEntry.rank || ''}
                    onChange={(e) => setSelectedEntry(prev => ({ ...prev, rank: parseInt(e.target.value) }))}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Name"
                    value={selectedEntry.name || ''}
                    onChange={(e) => setSelectedEntry(prev => ({ ...prev, name: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    value={selectedEntry.email || ''}
                    onChange={(e) => setSelectedEntry(prev => ({ ...prev, email: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Country"
                    value={selectedEntry.country || ''}
                    onChange={(e) => setSelectedEntry(prev => ({ ...prev, country: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label={
                      selectedEntry.leaderboardType === 'xmPoints' ? 'Points' :
                      selectedEntry.leaderboardType === 'xmPercentage' ? 'Percentage Growth' :
                      selectedEntry.leaderboardType === 'weltradeVolume' ? 'Lots Traded' :
                      'Overall Score'
                    }
                    value={
                      selectedEntry.points || selectedEntry.percentageGrowth || 
                      selectedEntry.lotsTraded || selectedEntry.overallScore || ''
                    }
                    onChange={(e) => {
                      const field = selectedEntry.leaderboardType === 'xmPoints' ? 'points' :
                                   selectedEntry.leaderboardType === 'xmPercentage' ? 'percentageGrowth' :
                                   selectedEntry.leaderboardType === 'weltradeVolume' ? 'lotsTraded' :
                                   'overallScore';
                      setSelectedEntry(prev => ({ ...prev, [field]: e.target.value }));
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Profit"
                    value={selectedEntry.profit || ''}
                    onChange={(e) => setSelectedEntry(prev => ({ ...prev, profit: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Number of Trades"
                    type="number"
                    value={selectedEntry.trades || ''}
                    onChange={(e) => setSelectedEntry(prev => ({ ...prev, trades: parseInt(e.target.value) }))}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Win Rate (%)"
                    type="number"
                    inputProps={{ min: 0, max: 100, step: 0.1 }}
                    value={selectedEntry.winRate || ''}
                    onChange={(e) => setSelectedEntry(prev => ({ ...prev, winRate: parseFloat(e.target.value) }))}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Verification Status</InputLabel>
                    <Select
                      value={selectedEntry.isVerified ? 'verified' : 'unverified'}
                      label="Verification Status"
                      onChange={(e) => setSelectedEntry(prev => ({ 
                        ...prev, 
                        isVerified: e.target.value === 'verified' 
                      }))}
                    >
                      <MenuItem value="verified">Verified</MenuItem>
                      <MenuItem value="unverified">Unverified</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} startIcon={<Cancel />}>
            Cancel
          </Button>
          <Button onClick={() => handleSaveEntry(false)} variant="contained" startIcon={<Save />}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Entry Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add New Leaderboard Entry</DialogTitle>
        <DialogContent>
          {selectedEntry && (
            <Stack spacing={3} sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Rank"
                    type="number"
                    value={selectedEntry.rank || ''}
                    onChange={(e) => setSelectedEntry(prev => ({ ...prev, rank: parseInt(e.target.value) }))}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Name"
                    value={selectedEntry.name || ''}
                    onChange={(e) => setSelectedEntry(prev => ({ ...prev, name: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    value={selectedEntry.email || ''}
                    onChange={(e) => setSelectedEntry(prev => ({ ...prev, email: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Country"
                    value={selectedEntry.country || ''}
                    onChange={(e) => setSelectedEntry(prev => ({ ...prev, country: e.target.value }))}
                  />
                </Grid>
              </Grid>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)} startIcon={<Cancel />}>
            Cancel
          </Button>
          <Button onClick={() => handleSaveEntry(true)} variant="contained" startIcon={<Save />}>
            Add Entry
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the entry for "{entryToDelete?.name}"? 
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteEntry} color="error" variant="contained">
            Delete Entry
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LeaderboardManagement; 